/**
 * One error type for every way an API call can fail.
 *
 * The API answers every non-2xx with the `errorEnvelope` shape from
 * `@wend/shared`, so the client parses that shape once, here, and the rest of
 * the app switches on `error.code` rather than matching strings. A response
 * that is *not* an envelope (a proxy's HTML 502, an empty body) still produces
 * an `ApiError` — with a generic message for its status, never the raw body.
 */
import { errorEnvelopeSchema, type ErrorCode, type FieldError } from '@wend/shared';

/** Key used for an error that belongs to the form as a whole, not one field. */
export const ROOT_FIELD = '_';

const GENERIC_MESSAGE: Readonly<Record<ErrorCode, string>> = {
  bad_request: 'The request could not be processed.',
  validation_failed: 'Please check the highlighted fields.',
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: 'You do not have access to that.',
  not_found: 'We could not find what you asked for.',
  conflict: 'That conflicts with something that already exists.',
  rate_limited: 'Too many attempts. Please wait a moment and try again.',
  internal: 'Something went wrong. Please try again.',
};

function codeForStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 429:
      return 'rate_limited';
    default:
      return status >= 500 ? 'internal' : 'bad_request';
  }
}

export interface ApiErrorInit {
  status: number;
  code: ErrorCode;
  message: string;
  fieldErrors?: readonly FieldError[];
  requestId?: string;
  retryAfterSeconds?: number;
  isNetworkError?: boolean;
  cause?: unknown;
}

export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached the API. */
  readonly status: number;
  readonly code: ErrorCode;
  readonly fieldErrors: readonly FieldError[];
  readonly requestId: string | undefined;
  readonly retryAfterSeconds: number | undefined;
  /** True for offline / DNS / CORS failures — nothing was ever answered. */
  readonly isNetworkError: boolean;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.fieldErrors = init.fieldErrors ?? [];
    this.requestId = init.requestId;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.isNetworkError = init.isNetworkError ?? false;
  }

  /** `fetch` rejected: offline, DNS failure, or a CORS pre-flight refusal. */
  static network(cause: unknown): ApiError {
    return new ApiError({
      status: 0,
      code: 'internal',
      message: 'Could not reach the Wend API. Check your connection and try again.',
      isNetworkError: true,
      cause,
    });
  }

  /** A response body that did not match the schema the caller expected. */
  static malformedResponse(cause: unknown): ApiError {
    return new ApiError({
      status: 0,
      code: 'internal',
      message: 'The server returned an unexpected response. Please try again.',
      cause,
    });
  }

  /** Reads the error envelope out of a non-2xx response. */
  static async fromResponse(response: Response): Promise<ApiError> {
    const fallbackCode = codeForStatus(response.status);
    const retryAfter = Number.parseInt(response.headers.get('Retry-After') ?? '', 10);

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      payload = undefined;
    }

    const parsed = errorEnvelopeSchema.safeParse(payload);

    if (!parsed.success) {
      // Flow's two 422s (slice 68, `flow-refusal.ts`) answer with a contract
      // body of their own — `{ message, issues }` / `{ message, checks }` —
      // not the envelope, because the editor needs the list as data. Their
      // `message` is the API's own sentence, so it is shown rather than the
      // generic line. Only a 422 is read this way: any other non-envelope body
      // (a proxy's 502 page) still gets the generic message, never raw text.
      const refusal = response.status === 422 ? refusalMessage(payload) : null;
      if (refusal !== null) {
        return new ApiError({ status: 422, code: 'validation_failed', message: refusal });
      }
      return new ApiError({
        status: response.status,
        code: fallbackCode,
        message: GENERIC_MESSAGE[fallbackCode],
        ...(Number.isFinite(retryAfter) ? { retryAfterSeconds: retryAfter } : {}),
      });
    }

    const { code, message, fieldErrors, requestId } = parsed.data.error;

    return new ApiError({
      status: response.status,
      code,
      message: message.trim() === '' ? GENERIC_MESSAGE[code] : message,
      ...(fieldErrors ? { fieldErrors } : {}),
      ...(requestId !== undefined ? { requestId } : {}),
      ...(Number.isFinite(retryAfter) ? { retryAfterSeconds: retryAfter } : {}),
    });
  }
}

/** The `message` of a Flow refusal body, when it has a usable one. */
function refusalMessage(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null || !('message' in payload)) return null;
  const message = (payload as { message: unknown }).message;
  if (typeof message !== 'string') return null;
  const trimmed = message.trim();
  return trimmed === '' || trimmed.length > 500 ? null : trimmed;
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * The message to show a person. Anything that is not an `ApiError` is a bug in
 * this app, so it gets the generic line rather than a stack-shaped string.
 */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  return GENERIC_MESSAGE.internal;
}

/**
 * Server-side field errors, keyed the same way the form's own zod errors are,
 * so a form can merge the two without caring which produced them. The first
 * message for a path wins.
 */
export function apiFieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error)) return {};

  const result: Record<string, string> = {};
  for (const fieldError of error.fieldErrors) {
    const key = fieldError.path === '' ? ROOT_FIELD : fieldError.path;
    if (result[key] === undefined) {
      result[key] = fieldError.message;
    }
  }
  return result;
}
